import React, { useEffect, useState, useRef } from "react";
import { CSVLink } from "react-csv";
import DataTable from "react-data-table-component";
import { DatePicker, Button, Row, Col, Tooltip, message } from "antd";
import { SearchOutlined, ReloadOutlined, FileExcelOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";

import { getRequest } from "../../../Utils/AppApi";

const ADMIN_JOIN_US_REPORT = "/admin/join-us-report";

// Helper function to format date only
const getExportFormat = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

// Helper function to format date and time together
const getUTCToLocalDateTimeFormat = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
};

// Helper function to convert date to query format
const queryDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

// Helper function to get today's date
const toDayDate = () => {
  return new Date();
};

function JoinUsReport() {
  const history = useHistory();
  const csvLink = useRef();

  const limit = 10;
  const dateFormat = "DD-MM-YYYY";

  const [isLoading, setLoading] = useState(false);
  const [searchMobile, setSearchMobile] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [getData, setData] = useState([]);
  const [getCsvData, setCsvData] = useState([]);
  const [getTotalRecord, setTotalRecord] = useState(0);
  const [page, setPage] = useState(1);
  const [isLimit, setLimit] = useState(limit);

  const columns = [
    {
      name: "Date & Time",
      cell: (row) => {
        const dateTime = getUTCToLocalDateTimeFormat(row.createdAt);
        return (
          <Tooltip title={dateTime}>
            <span style={{ whiteSpace: 'nowrap' }}>{dateTime}</span>
          </Tooltip>
        );
      },
      width: "180px",
      center: true,
      sortable: true,
    },
    {
      name: "Full Name",
      selector: (row) => row.fullName || "-",
      width: "200px",
      center: true,
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.email || "-",
      width: "200px",
      center: true,
    },
    {
      name: "Mobile",
      selector: (row) => row.mobile || "-",
      width: "140px",
      center: true,
      sortable: true,
    },
    {
      name: "Referral Mobile",
      selector: (row) => row.referralMobile || "-",
      width: "160px",
      center: true,
    },
    {
      name: "City",
      selector: (row) => row.city || "-",
      width: "140px",
      center: true,
    },
    {
      name: "State",
      selector: (row) => row.state || "-",
      width: "140px",
      center: true,
    },
    {
      name: "Pincode",
      selector: (row) => row.pincode || "-",
      width: "120px",
      center: true,
    },
  ];

  const objectToQueryString = (obj) => {
    const str = [];
    for (let p in obj) {
      if (obj.hasOwnProperty(p) && obj[p] !== null && obj[p] !== "" && obj[p] !== undefined) {
        str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
      }
    }
    return str.join("&");
  };

  const getDataReports = async (newLimit) => {
    setLoading(true);

    let params = { page: page, limit: isLimit };

    if (newLimit) {
      setLimit(newLimit);
      params.limit = newLimit;
      params.page = 1;
      setPage(1);
    }

    if (startDate) {
      params.startDate = queryDate(startDate);
    }
    if (endDate) {
      params.endDate = queryDate(endDate);
    }

    if (searchMobile) {
      params.searchMobile = searchMobile;
    }

    const queryString = objectToQueryString(params);
    const apiUrl = `${ADMIN_JOIN_US_REPORT}?${queryString}`;
    
    console.log("API URL:", apiUrl);

    try {
      const response = await getRequest(apiUrl, history);
      console.log("API Response:", response);
      
      if (response && response.success === true) {
        // Handle different response structures
        let docs = [];
        let total = 0;
        
        if (response.data && response.data.docs) {
          docs = response.data.docs;
          total = response.data.totalDocs || 0;
        } else if (response.data && Array.isArray(response.data)) {
          docs = response.data;
          total = response.data.length;
        } else if (Array.isArray(response)) {
          docs = response;
          total = response.length;
        } else if (response.data && response.data.data && response.data.data.docs) {
          docs = response.data.data.docs;
          total = response.data.data.totalDocs || 0;
        }
        
        setData(docs);
        setTotalRecord(total);
        setPage(response.data?.page || 1);
        
        if (docs.length === 0) {
          message.info("No records found");
        }
      } else {
        setData([]);
        setTotalRecord(0);
        setPage(1);
        message.error(response?.message || "Failed to fetch data");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("Failed to fetch data");
      setData([]);
      setTotalRecord(0);
    } finally {
      setLoading(false);
    }
  };

  const getCSVDataList = async () => {
    let params = { exportsCsv: true };

    if (startDate) {
      params.startDate = queryDate(startDate);
    }
    if (endDate) {
      params.endDate = queryDate(endDate);
    }
    if (searchMobile) {
      params.searchMobile = searchMobile;
    }

    const queryString = objectToQueryString(params);
    const apiUrl = `${ADMIN_JOIN_US_REPORT}?${queryString}`;

    try {
      const response = await getRequest(apiUrl, history);
      
      if (response && response.success === true) {
        let csvdata = [];
        let dataArray = [];
        
        if (response.data && Array.isArray(response.data)) {
          dataArray = response.data;
        } else if (response.data && response.data.docs) {
          dataArray = response.data.docs;
        } else if (Array.isArray(response)) {
          dataArray = response;
        }
        
        dataArray.forEach((e) => {
          csvdata.push({
            "Date & Time": getUTCToLocalDateTimeFormat(e.createdAt),
            "Full Name": e.fullName || "-",
            Email: e.email || "-",
            Mobile: e.mobile || "-",
            City: e.city || "-",
            State: e.state || "-",
            Pincode: e.pincode || "-",
            "Referral Mobile": e.referralMobile || "-",
          });
        });
        
        setCsvData(csvdata);
        setTimeout(() => {
          csvLink.current.link.click();
        }, 500);
        message.success("CSV exported successfully");
      } else {
        message.error("No data to export");
      }
    } catch (error) {
      console.error("Error exporting CSV:", error);
      message.error("Failed to export CSV");
    }
  };

  const handlePageChange = (page) => {
    setPage(page);
  };

  const handleRowsPerPageChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
    getDataReports(newLimit);
  };

  const onStartDateChange = (date) => {
    setStartDate(date);
  };

  const onEndDateChange = (date) => {
    setEndDate(date);
  };

  const handleSearch = () => {
    setPage(1);
    getDataReports();
  };

  const handleClear = () => {
    setSearchMobile("");
    setStartDate(null);
    setEndDate(null);
    setTimeout(() => {
      setPage(1);
      getDataReports();
    }, 100);
  };

  useEffect(() => {
    getDataReports();
  }, [page]);

  return (
    <div>
      <div className="content-wrapper pt-2">
        <div className="content-header my-0 py-0">
          <div className="container-fluid">
            <Row gutter={16} align="middle">
              <Col xs={24} sm={12} md={8}>
                <h1 className="page-title text-blue font-merriweather" style={{ marginBottom: 0 }}>
                  Join Us <span className="text-red">Report</span>
                </h1>
              </Col>
            </Row>
          </div>
        </div>

        <div className="content">
          <div className="container-fluid pt-2 pb-5">
            <div className="card col-md-12">
              <div className="card-body">
                {/* Filter Row */}
                <Row gutter={[16, 16]} align="bottom">
                  <Col xs={24} sm={12} md={6} lg={4}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="mb-1 text-blue">From Date</label>
                      <DatePicker
                        className="form-control form-control-border w-100"
                        format={dateFormat}
                        value={startDate}
                        onChange={onStartDateChange}
                        placeholder="Start Date"
                        style={{ width: "100%", borderRadius: "8px" }}
                      />
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={6} lg={4}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="mb-1 text-blue">To Date</label>
                      <DatePicker
                        className="form-control form-control-border w-100"
                        format={dateFormat}
                        value={endDate}
                        onChange={onEndDateChange}
                        placeholder="End Date"
                        style={{ width: "100%", borderRadius: "8px" }}
                      />
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={8} lg={5}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="mb-1 text-blue">Mobile Number</label>
                      <input
                        type="text"
                        className="form-control form-control-border"
                        placeholder="Search by mobile number"
                        value={searchMobile}
                        onChange={(e) => setSearchMobile(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                        style={{ width: "100%", borderRadius: "8px" }}
                      />
                    </div>
                  </Col>

                  <Col xs={12} sm={6} md={4} lg={3}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="mb-1 text-blue">&nbsp;</label>
                      <div>
                        <Button
                          type="primary"
                          onClick={handleSearch}
                          style={{
                            borderRadius: "8px",
                            width: "100%",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                          }}
                        >
                          <SearchOutlined /> Search
                        </Button>
                      </div>
                    </div>
                  </Col>

                  <Col xs={12} sm={6} md={4} lg={3}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="mb-1 text-blue">&nbsp;</label>
                      <div>
                        <Button
                          onClick={handleClear}
                          style={{
                            borderRadius: "8px",
                            width: "100%",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                          }}
                        >
                          <ReloadOutlined /> Clear
                        </Button>
                      </div>
                    </div>
                  </Col>

                  <Col xs={24} sm={24} md={8} lg={5}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="mb-1 text-blue">&nbsp;</label>
                      <div className="float-sm-right">
                        <Button
                          type="primary"
                          onClick={getCSVDataList}
                          style={{
                            borderRadius: "8px",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "8px",
                            backgroundColor: "#52c41a",
                            borderColor: "#52c41a",
                          }}
                        >
                          <FileExcelOutlined /> Export to CSV
                        </Button>
                        <CSVLink
                          data={getCsvData}
                          filename={`join-us-report-${queryDate(startDate) || "all"}-to-${queryDate(endDate) || "all"}`}
                          className="hidden"
                          ref={csvLink}
                          target="_blank"
                        />
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Data Table */}
                <div className="container-fluid pt-3 pb-2">
                  <DataTable
                    title=""
                    columns={columns}
                    data={getData}
                    highlightOnHover
                    pagination
                    paginationServer
                    progressPending={isLoading}
                    paginationTotalRows={getTotalRecord}
                    onChangeRowsPerPage={handleRowsPerPageChange}
                    onChangePage={handlePageChange}
                    paginationRowsPerPageOptions={[5, 10, 20, 50, 100]}
                    noDataComponent="No Join Us records found"
                    style={{
                      borderRadius: "8px",
                      overflow: "hidden",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default JoinUsReport;